import { isObject, isString } from '@vue/shared'
import {
  buildProps,
  definePropType,
  iconPropType,
  isUndefined,
  mutable,
} from '@bigin/utils'
import { UPDATE_MODEL_EVENT } from '@bigin/constants'
import { useSizeProp } from '@bigin/hooks'
import { isCountryAvailable } from './utils'
import type {
  CountryCallingCode,
  CountryCode,
  NationalNumber,
  NumberType,
} from 'libphonenumber-js'
import type PhoneInput from './phone-input.vue'
import type { ExtractPropTypes, PropType, StyleValue } from 'vue'

import type locales from './constants/locales'

export type Result = {
  isValid: boolean
  countryCode?: CountryCode
  countryCallingCode?: CountryCallingCode
  nationalNumber?: NationalNumber
  type?: NumberType
  formatInternational?: string
  formatNational?: string
  uri?: string
  e164?: string
}

export type Translations = typeof locales

export type Country = {
  iso2: CountryCode
  dialCode: CountryCallingCode
  name: string
}

export const phoneInputProps = buildProps({
  id: {
    type: String,
    default: undefined,
  },
  size: useSizeProp,
  disabled: Boolean,
  type: {
    type: String,
    default: 'phone',
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
  formatter: {
    type: Function,
  },
  parser: {
    type: Function,
  },
  placeholder: {
    type: String,
  },
  form: {
    type: String,
    default: '',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  showPassword: {
    type: Boolean,
    default: false,
  },
  showWordLimit: {
    type: Boolean,
    default: false,
  },
  suffixIcon: {
    type: iconPropType,
    default: '',
  },
  suffixClass: {
    type: String,
    default: '',
  },
  prependClass: {
    type: String,
    default: '',
  },
  appendClass: {
    type: String,
    default: '',
  },
  containerRole: {
    type: String,
    default: undefined,
  },
  label: {
    type: String,
    default: undefined,
  },
  tabindex: {
    type: [String, Number],
    default: 0,
  },
  validateEvent: {
    type: Boolean,
    default: true,
  },
  inputStyle: {
    type: definePropType<StyleValue>([Object, Array, String]),
    default: () => mutable({} as const),
  },
  inputClass: {
    type: String,
    default: '',
  },
  rounded: {
    type: Boolean,
    default: false,
  },
  large: {
    type: Boolean,
    default: false,
  },
  small: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: String,
    validator: (prop: string) => {
      return typeof prop === 'string' || prop === undefined
    },
    default: undefined,
  },
  defaultPhoneNumber: { type: String, default: undefined },
  defaultCountryCode: {
    type: String as PropType<CountryCode>,
    default: undefined,
    validator: (code: CountryCode) => {
      return isCountryAvailable(code)
    },
  },
  preferredCountries: {
    type: Array as PropType<CountryCode[]>,
    default: undefined,
  },
  ignoredCountries: {
    type: Array as PropType<CountryCode[]>,
    default: undefined,
  },
  onlyCountries: {
    type: Array as PropType<CountryCode[]>,
    default: undefined,
  },
  translations: {
    type: Object as PropType<Translations>,
    default: undefined,
  },
  noFlags: { type: Boolean, default: false },
  noExample: { type: Boolean, default: false },
  noUseBrowserLocale: { type: Boolean, default: false },
  fetchCountry: { type: Boolean, default: false },
  noCountrySelector: { type: Boolean, default: false },
  showCodeOnList: { type: Boolean, default: false },
  customCountriesList: {
    type: Object as PropType<Record<CountryCode, string>>,
    default: undefined,
  },
  showCountriesFilter: {
    type: Boolean,
    default: true,
  },
} as const)
export type PhoneInputProps = ExtractPropTypes<typeof phoneInputProps>

export const phoneInputEmits = {
  [UPDATE_MODEL_EVENT]: (value: string | undefined) =>
    isString(value) || isUndefined(value),
  input: (value: string) => isString(value),
  change: (value: string) => isString(value),
  focus: (evt: FocusEvent) => evt instanceof FocusEvent,
  blur: (evt: FocusEvent) => evt instanceof FocusEvent,
  clear: () => true,
  mouseleave: (evt: MouseEvent) => evt instanceof MouseEvent,
  mouseenter: (evt: MouseEvent) => evt instanceof MouseEvent,
  keydown: (evt: KeyboardEvent | Event) => evt instanceof Event,
  compositionstart: (evt: CompositionEvent) => evt instanceof CompositionEvent,
  compositionupdate: (evt: CompositionEvent) => evt instanceof CompositionEvent,
  compositionend: (evt: CompositionEvent) => evt instanceof CompositionEvent,
  countryCode: (value: string) => isString(value),
  update: (value: Partial<Result>) => isObject(value),
}
export type PhoneInputEmits = typeof phoneInputEmits

export type PhoneInputInstance = InstanceType<typeof PhoneInput>
